import Cropper from "cropperjs"; // Guide and examples https://fengyuanchen.github.io/cropperjs/

let cropperList = []

// create the template for the cropper
// uniqueId is used to identify the selection in the template with multiple images
// initial is used for the initial selection because of a conflict of initial-coverage with setting the values from the inputs
function previewCropperTemplate(uniqueId = "imageSelection", type){
    // tours have aspectRatio 3:2 and stations have aspectRatio 2:1
    let aspectRatio = (type === "tour") ? 1.5 : 2
    let overlayHeight = (type === "tour") ? "h-1/2" : "h-1/3"

    let viewerHeight = 128
    let viewerWidth = viewerHeight * aspectRatio
    return `
        <div class="flex w-full h-full">
            <cropper-canvas background>
                <cropper-image rotatable scalable="false" ></cropper-image>
                <cropper-shade hidden></cropper-shade>
                <cropper-selection id="${uniqueId}" aspect-ratio=${aspectRatio} movable resizable outlined>
                    <cropper-handle action="move" theme-color="rgba(255, 255, 255, 0.35)"></cropper-handle>
                    <cropper-handle action="n-resize"></cropper-handle>
                    <cropper-handle action="e-resize"></cropper-handle>
                    <cropper-handle action="s-resize"></cropper-handle>
                    <cropper-handle action="w-resize"></cropper-handle>  
                </cropper-selection>
            </cropper-canvas>
            <div class="ml-6" style="width: ${viewerWidth}px;">
                <div class="text-bold">${I18n.t('js.used_section')}</div>
                <div class="cropper-viewers relative rounded-xl overflow-hidden" style="width: ${viewerWidth}px; height: ${viewerHeight}px;">
                    <cropper-viewer selection="#${uniqueId}"></cropper-viewer>
                    <div class="bg-black absolute w-full bottom-0 opacity-50 ${overlayHeight}"></div>
                </div>
            </div>
        </div>
    `
}

export function createPreviewCropper(imageList, type, initial) {
    // destroy old croppers
    cropperList.forEach(cropper => cropper.destroy())
    cropperList = []
    for (let i = 0; i < imageList.length; i++) {
        let image = imageList[i]
        if (image.src.includes("image_placeholder")){
            continue;
        }

        if (image.complete) {
            // on initial loading or second time loading the image, it is present from the start
            initializeCropper(image, i, initial, type)
        } else {
            // the image is not present immediately when loading with AJAX
            image.addEventListener('load', () => {initializeCropper(image, i, initial, type)}, {once: true})
        }
    }
}

function initializeCropper(image, selectionId, initial = false, type) {

    // get ratio from original image
    let width = image.naturalWidth
    let height = image.naturalHeight
    let ratio = width / height

    let uniqueId = `cropper-selection-${selectionId}`
    let cropper = new Cropper(image, {
        // use the template with unique id for selection. Otherwise, problems with querySelector of viewer
        template: previewCropperTemplate(uniqueId, type),
    })
    let cropperCanvas = cropper.getCropperCanvas()
    // remove min-width and min-height from cropper canvas to prevent the canvas from ignoring the ratio
    cropperCanvas.style.minWidth = '0'
    cropperCanvas.style.minHeight = '0'
    // set canvas width to 400px which is usually used
    let canvasWidth = 400
    let canvasHeight = canvasWidth / ratio
    // limit canvas height to 224px
    if (canvasHeight > 224) {
        canvasHeight = 224
        canvasWidth = canvasHeight * ratio
    }
    cropperCanvas.style.width = `${canvasWidth}px`
    cropperCanvas.style.height = `${canvasHeight}px`

    cropper.container.dataset["pixelRatio"] = width / canvasWidth
    initializeSelection(cropper, initial, type)

    let selection = cropper.getCropperSelection()
    selection.addEventListener('change', (e) => {
        if(selectionInBounds(cropper)) {
            selection.$toCanvas()
            changeInputs(cropper.container, selection, type)
        } else {
            e.preventDefault()
        }
    })

    cropperList.push(cropper)
}

function initializeSelection(cropper, initial, type) {
    let selection = cropper.getCropperSelection()
    let container = cropper.container
    let language = container.dataset["language"]
    let pixelRatio = container.dataset["pixelRatio"]
    let aspectRatio = (type === "tour") ? 1.5 : 2

    // set initial selection from inputs
    if (initial) {
        selection.x = document.getElementById(`${type}_preview_images_${language}_start_x`).value / pixelRatio
        selection.y = document.getElementById(`${type}_preview_images_${language}_start_y`).value / pixelRatio
        selection.width = document.getElementById(`${type}_preview_images_${language}_width`).value / pixelRatio
        selection.height = document.getElementById(`${type}_preview_images_${language}_height`).value / pixelRatio
    } else {
        // set initial selection from canvas/image width and height
        let cropperCanvas = cropper.getCropperCanvas()

        let canvasWidth = parseInt(cropperCanvas.style.width)
        let canvasHeight = parseInt(cropperCanvas.style.height)
        let canvasRatio = canvasWidth / canvasHeight

        if (canvasRatio < aspectRatio) {
            // image is higher than the selection, so the selection is centered on the y-axis
            selection.width = canvasWidth
            selection.height = canvasWidth / aspectRatio
            selection.x = 0
            selection.y = (canvasHeight - selection.height) / 2
        } else {
            // image is wider than the selection, so the selection is centered on the x-axis
            selection.height = canvasHeight
            selection.width = canvasHeight * aspectRatio
            selection.y = 0
            selection.x = (canvasWidth - selection.width) / 2
        }
    }

}

// Ensure selection parameters are insde the image boundaries
function selectionInBounds(cropper) {
    let cropperCanvas = cropper.getCropperCanvas()
    let selection = cropper.getCropperSelection()
    let canvasWidth = parseInt(cropperCanvas.style.width)
    let canvasHeight = parseInt(cropperCanvas.style.height)
    
    console.log("selectionInBounds: ",selection)
    let returnValue = true
    // Minimum width value
    if (selection.width < 0) {
        selection.width = 0
        returnValue = false
    }
    // Maximum width value
    if (selection.width > canvasWidth) {
        // aspect ratio can slightly change height and width if they don't match the ratio or are no integers
        // Make sure height is an integer.
        // Otherwise, an odd canvasWidth can block the selection change with aspectRatio = 2
        selection.height = Math.floor(canvasWidth / selection.aspectRatio)
        selection.width = selection.height * selection.aspectRatio
        selection.x = 0
        returnValue = false
    }
    // Minimum height value
    if (selection.height < 0) {
        selection.height = 0
        returnValue = false
    }
    // Maximum height value
    if (selection.height > canvasHeight) {
        selection.height = canvasHeight
        selection.width = canvasHeight * selection.aspectRatio
        selection.y = 0
        returnValue = false
    }
    // selection.x and selection.y are the coordinates of the top left corner of the selection
    // Minimum x value
    if (selection.x < 0) {
        selection.x = 0
        returnValue = false
    }
    // Maximum x value
    if (selection.x + selection.width > canvasWidth) {
        selection.x = canvasWidth - selection.width
        returnValue = false
    }
    // Minimum y value
    if (selection.y < 0) {
        selection.y = 0
        returnValue = false
    }
    // Maximum y value
    if (selection.y + selection.height > canvasHeight) {
        selection.y = canvasHeight - selection.height
        returnValue = false
    }
    
    return returnValue
}

function changeInputs(container, selection, type) {
    let language = container.dataset["language"]
    let pixelRatio = container.dataset["pixelRatio"] || 1
    document.getElementById(`${type}_preview_images_${language}_start_x`).value = selection.x * pixelRatio
    document.getElementById(`${type}_preview_images_${language}_start_y`).value = selection.y * pixelRatio
    document.getElementById(`${type}_preview_images_${language}_width`).value = selection.width * pixelRatio
    document.getElementById(`${type}_preview_images_${language}_height`).value = selection.height * pixelRatio
}